import { useState, useEffect, useRef } from 'react';

const queries = [
  "Souveraineté européenne",
  "Politique migratoire",
  "Déclin démographique",
];

const SearchAnimation = () => {
  const [text, setText] = useState('');
  const [queryIndex, setQueryIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting' | 'waiting'>('waiting');
  const [cursorVisible, setCursorVisible] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cursor blink — slow, elegant
  useEffect(() => {
    const interval = setInterval(() => setCursorVisible(v => !v), 600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const currentQuery = queries[queryIndex];

    switch (phase) {
      case 'waiting':
        timeoutRef.current = setTimeout(() => setPhase('typing'), 1200);
        break;

      case 'typing':
        if (text.length < currentQuery.length) {
          const delay = 120 + Math.random() * 100; // slower, more human
          timeoutRef.current = setTimeout(() => {
            setText(currentQuery.slice(0, text.length + 1));
          }, delay);
        } else {
          setPhase('pausing');
        }
        break;

      case 'pausing':
        timeoutRef.current = setTimeout(() => setPhase('deleting'), 3000); // long pause to read
        break;

      case 'deleting':
        if (text.length > 0) {
          timeoutRef.current = setTimeout(() => {
            setText(text.slice(0, -1));
          }, 50);
        } else {
          setQueryIndex((queryIndex + 1) % queries.length);
          setPhase('waiting');
        }
        break;
    }

    return () => clearTimeout(timeoutRef.current);
  }, [text, phase, queryIndex]);

  return (
    <div className="mt-6 mb-2">
      {/* Window chrome — makes it look like a UI mockup */}
      <div className="rounded-lg border border-primary/20 bg-primary/[0.03] overflow-hidden max-w-[300px] shadow-sm">
        {/* Title bar */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-primary/10 bg-primary/[0.04]">
          <span className="w-[7px] h-[7px] rounded-full bg-primary/20" />
          <span className="w-[7px] h-[7px] rounded-full bg-primary/15" />
          <span className="w-[7px] h-[7px] rounded-full bg-primary/10" />
          <span className="text-[10px] text-primary/40 ml-2 select-none tracking-wide">ia.polemia.com</span>
        </div>
        
        {/* Search bar */}
        <div className="px-3 h-[82px] flex items-center">
          <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-primary/15 bg-background/60">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            className="text-primary/50 shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            
            <span className="text-sm text-muted-foreground/60 font-light select-none whitespace-nowrap">
              {text}
              <span
                className="inline-block w-[1.5px] h-[13px] bg-primary/40 ml-[1px] align-middle"
                style={{ 
                  opacity: cursorVisible ? 1 : 0,
                  transition: 'opacity 0.1s ease'
                }}
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAnimation;
